import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import SearchAndFilters from './components/SearchAndFilters';
import CandidatesTable from './components/CandidatesTable';
import CandidatesMobileView from './components/CandidatesMobileView';
import CandidatesPagination from './components/CandidatesPagination';
import AddCandidateModal from './components/AddCandidateModal';
import AddCandidateButton, { getCandidateLimitTooltip } from './components/AddCandidateButton';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useCandidates, useProfile } from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';
import {
  createCandidate,
  findCandidatesByLinkedinUrls,
  getCandidatesStats,
  getPrioritizedCandidatesCount,
  uploadCandidatesToZohoList,
} from '../../lib/api';
import { toCandidateDbPayload } from '../../lib/utils/csvParser';
import {
  sortCandidatesByLastCheck,
} from '../../lib/utils/candidateListSort';
import { format } from 'date-fns';
import { parseBoolean } from '../../lib/utils/boolean';
import { getCandidateListStatus } from '../../lib/utils/candidateStatus';
import { formatCandidateDisplayValue } from '../../lib/utils/formatCandidateField';
import {
  buildCandidatesListSearchParams,
  parseCandidatesListSearchParams,
} from '../../lib/utils/candidateListRoutes';
import { needsSubscriptionSelection } from '../../lib/utils/subscriptionAccess';
import { planCanPrioritize, planHasAtsIntegration, planIsTopTier } from '../../lib/utils/planEntitlements';
import { isZohoAtsConfigured } from '../../lib/utils/ats';
import { isZohoUploadFieldsEmpty } from '../../lib/utils/zohoUploadFields';
import ZohoUploadResult from '../../components/ZohoUploadResult';
import Tooltip from '../../components/ui/Tooltip';

const MAX_PRIORITIZED = 5;
const PAGE_SIZE = 20;

const CandidatesList = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const statusFilterLabels = {
    pending: t('candidates.statusPending'),
    'active-seeker': t('candidates.statusActiveSeeker'),
    'potential-seeker': t('candidates.statusPotentialSeeker'),
    stable: t('candidates.statusStable'),
    unavailable: t('candidates.statusUnavailable'),
    manual: t('candidates.statusManual'),
    prioritized: t('candidates.statusPrioritized'),
  };

  const contactFilterLabels = {
    'linkedin-blocked': t('candidates.contactLinkedinBlocked'),
    'phone-missing': t('candidates.contactPhoneMissing'),
    'phone-available': t('candidates.contactPhoneAvailable'),
    'linkedin-available': t('candidates.contactLinkedinAvailable'),
  };
  const { profile, refetch: refetchProfile } = useProfile();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(
    () => parseCandidatesListSearchParams(searchParams),
    [searchParams],
  );
  const syncedUrlParamsRef = useRef(searchParams.toString());

  const [searchInput, setSearchInput] = useState(urlState.searchTerm);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(urlState.searchTerm);
  const [statusFilter, setStatusFilter] = useState(urlState.statusFilter);
  const [contactFilter, setContactFilter] = useState(urlState.contactFilter);
  const [lastCheckSort, setLastCheckSort] = useState(urlState.lastCheckSort);
  const [currentPage, setCurrentPage] = useState(urlState.currentPage);
  const [showAddModal, setShowAddModal] = useState(false);
  const [candidatesStats, setCandidatesStats] = useState({ total: 0 });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadNotice, setUploadNotice] = useState(null);
  
  const maxCandidates = profile?.max_candidates || 50;
  const currentCandidateCount = candidatesStats.total || 0;
  const remainingCandidates = profile?.candidates_remaining != null
    ? Math.max(0, profile.candidates_remaining)
    : Math.max(0, maxCandidates - currentCandidateCount);
  const isCandidateLimitReached = remainingCandidates <= 0;
  const canPrioritize = planCanPrioritize(profile?.subscription_plan);
  const isTopTierPlan = planIsTopTier(profile?.subscription_plan);
  const isRestricted = needsSubscriptionSelection(profile);
  const hasAtsIntegration = planHasAtsIntegration(profile?.subscription_plan);
  const isZohoConnected = isZohoAtsConfigured(profile);
  const hasNoUploadFields = isZohoUploadFieldsEmpty(profile?.zoho_upload_fields);
  
  useEffect(() => {
    const currentParams = searchParams.toString();
    if (currentParams === syncedUrlParamsRef.current) {
      return;
    }

    syncedUrlParamsRef.current = currentParams;
    setSearchInput(urlState.searchTerm);
    setAppliedSearchTerm(urlState.searchTerm);
    setStatusFilter(urlState.statusFilter);
    setContactFilter(urlState.contactFilter);
    setLastCheckSort(urlState.lastCheckSort);
    setCurrentPage(urlState.currentPage);
  }, [searchParams, urlState]);

  useEffect(() => {
    const nextParams = buildCandidatesListSearchParams({
      searchTerm: appliedSearchTerm,
      statusFilter,
      contactFilter,
      lastCheckSort,
      currentPage,
    });
    const nextParamsString = nextParams.toString();

    if (nextParamsString === syncedUrlParamsRef.current) {
      return;
    }

    syncedUrlParamsRef.current = nextParamsString;
    setSearchParams(nextParams, { replace: true });
  }, [
    appliedSearchTerm,
    statusFilter,
    contactFilter,
    lastCheckSort,
    currentPage,
    setSearchParams,
  ]);

  const handleApplySearch = () => {
    setAppliedSearchTerm(searchInput.trim());
    setCurrentPage(0);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(0);
  };

  const handleContactFilterChange = (value) => {
    setContactFilter(value);
    setCurrentPage(0);
  };

  const handleLastCheckSortChange = (value) => {
    setLastCheckSort(value);
    setCurrentPage(0);
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    setAppliedSearchTerm('');
    setStatusFilter('all');
    setContactFilter('all');
    setCurrentPage(0);
  };
  
  // Build filters object
  const filters = useMemo(() => ({
    search: appliedSearchTerm,
    status: statusFilter === 'all' || statusFilter === 'prioritized' ? undefined : statusFilter,
    prioritize: statusFilter === 'prioritized' ? true : undefined,
    contact: contactFilter === 'all' ? undefined : contactFilter,
  }), [appliedSearchTerm, statusFilter, contactFilter]);
  
  // Fetch real candidates from database with debounced search
  const { candidates: dbCandidates, loading, error, updateCandidate, deleteCandidate, refetch } = useCandidates(filters);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.id) return;

      try {
        const stats = await getCandidatesStats(user.id);
        setCandidatesStats(stats || { total: 0 });
      } catch (err) {
        console.error('Error fetching candidates stats:', err);
      }
    };

    fetchStats();
  }, [user?.id, dbCandidates?.length]);

  // Transform database candidates to match UI format
  const transformedCandidates = useMemo(() => {
    if (!dbCandidates) return [];
    
    return dbCandidates.map(candidate => {
      const status = getCandidateListStatus(candidate);
      
      // Check if candidate is new (created in last 7 days)
      const isNew = candidate.created_at && 
        (new Date() - new Date(candidate.created_at)) < 7 * 24 * 60 * 60 * 1000;
      
      // Format last scraped date
      const lastCheckDate = candidate.scraped_time 
        ? format(new Date(candidate.scraped_time), 'dd-MM-yyyy')
        : 'N/A';
      
      return {
        id: candidate.id,
        profileImage: candidate.avatar || null,
        profileImageAlt: `${candidate.full_name} profile`,
        fullName: candidate.full_name || 'Unknown',
        currentRole: formatCandidateDisplayValue(candidate.current_position || 'Not specified'),
        jobTitle: formatCandidateDisplayValue(
          candidate.current_title || candidate.current_position || 'Not specified',
        ),
        companyName: formatCandidateDisplayValue(candidate.current_company || 'Not specified'),
        status: status,
        isInvalidLink: parseBoolean(candidate.invalid_link),
        isManualOpenToWork: parseBoolean(candidate.is_open_to_work_manual),
        zohoId: String(candidate.zoho_id ?? '').trim() || null,
        isFromZoho: Boolean(String(candidate.zoho_id ?? '').trim()),
        hasScraped: Boolean(candidate.scraped_time && String(candidate.scraped_time).trim()),
        lastCheckDate: lastCheckDate,
        isPrioritized: candidate.prioritize || false,
        phone: candidate.phone,
        isNew: isNew,
        // Keep original data for updates
        _original: candidate
      };
    });
  }, [dbCandidates]);

  const sortedCandidates = useMemo(
    () => sortCandidatesByLastCheck(transformedCandidates, lastCheckSort),
    [transformedCandidates, lastCheckSort],
  );

  const totalCount = sortedCandidates.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(currentPage, pageCount - 1);

  const paginatedCandidates = useMemo(() => {
    const start = safePage * PAGE_SIZE;
    return sortedCandidates.slice(start, start + PAGE_SIZE);
  }, [sortedCandidates, safePage]);

  const selectedCount = selectedIds.size;
  const selectedZohoIds = useMemo(() => (
    sortedCandidates
      .filter((candidate) => selectedIds.has(candidate.id) && candidate.zohoId)
      .map((candidate) => candidate.zohoId)
  ), [sortedCandidates, selectedIds]);
  const selectedZohoCount = selectedZohoIds.length;

  useEffect(() => {
    const validIds = new Set(sortedCandidates.map((candidate) => candidate.id));
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [sortedCandidates]);

  useEffect(() => {
    if (currentPage !== safePage) {
      setCurrentPage(safePage);
    }
  }, [currentPage, safePage]);

  const handleTogglePriority = async (candidateId) => {
    const candidate = transformedCandidates.find(c => c.id === candidateId);
    if (!candidate) return;
    
    const newPriorityValue = !candidate.isPrioritized;
    
    // When adding priority: check limit (max 5 per user)
    if (newPriorityValue && user?.id) {
      try {
        const prioritizedCount = await getPrioritizedCandidatesCount(user.id);
        if (prioritizedCount >= MAX_PRIORITIZED) {
          setShowLimitDialog(true);
          return;
        }
      } catch (err) {
        // Column may not exist yet — run migration 003_add_priority_to_candidates.sql
        console.warn('getPrioritizedCandidatesCount failed:', err);
      }
    }
    
    const { error } = await updateCandidate(candidateId, {
      prioritize: newPriorityValue
    }, { updateTimestamp: false });
    
    if (error) {
      console.error('Failed to update priority:', error);
    }
  };

  const handleBulkUploadToZoho = async () => {
    if (!user?.id || bulkUploading || hasNoUploadFields) return;
    if (selectedZohoIds.length === 0) {
      setUploadNotice({ type: 'error', message: t('candidates.uploadNoZohoSelected') });
      return;
    }

    setUploadNotice(null);
    setUploadResult(null);
    setBulkUploading(true);
    try {
      const result = await uploadCandidatesToZohoList(user.id, selectedZohoIds);
      setUploadResult(result);
    } catch (err) {
      console.error('Failed to upload selected candidates to Zoho:', err);
      setUploadNotice({
        type: 'error',
        message: err?.message || t('candidates.uploadFailed'),
      });
    } finally {
      setBulkUploading(false);
    }
  };

  const handleDeleteCandidate = async (candidateId) => {
    const { error } = await deleteCandidate(candidateId);
    if (error) {
      console.error('Failed to delete candidate:', error);
      return;
    }
    setSelectedIds((prev) => {
      if (!prev.has(candidateId)) return prev;
      const next = new Set(prev);
      next.delete(candidateId);
      return next;
    });
    await refetchProfile?.();
    const stats = await getCandidatesStats(user.id);
    setCandidatesStats(stats || { total: 0 });
  };

  const handleToggleSelect = (candidateId) => {
    if (!candidateId) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(candidateId)) next.delete(candidateId);
      else next.add(candidateId);
      return next;
    });
  };

  const handleToggleSelectAll = (pageIds = []) => {
    const allIds = sortedCandidates.map((candidate) => candidate.id).filter(Boolean);

    setSelectedIds((prev) => {
      const allSelected = allIds.length > 0 && allIds.every((id) => prev.has(id));
      const allPageSelected = pageIds.length > 0 && pageIds.every((id) => prev.has(id));

      // 1st click → current page, 2nd → all filtered candidates, 3rd → clear
      if (allSelected) return new Set();
      if (allPageSelected) return new Set(allIds);
      return new Set(pageIds);
    });
  };

  const handleBulkDeleteConfirm = async () => {
    if (bulkDeleting) return;
    const ids = [...selectedIds];
    if (ids.length === 0) {
      setShowBulkDeleteConfirm(false);
      return;
    }

    setBulkDeleting(true);
    try {
      const results = await Promise.all(ids.map((id) => deleteCandidate(id)));
      const failed = results.filter((result) => result?.error);
      if (failed.length > 0) {
        console.error('Failed to delete some candidates:', failed);
      }
      setSelectedIds(new Set());
      setShowBulkDeleteConfirm(false);
      await refetchProfile?.();
      if (user?.id) {
        const stats = await getCandidatesStats(user.id);
        setCandidatesStats(stats || { total: 0 });
      }
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleAddCandidate = async (candidateData) => {
    if (!user?.id) {
      throw new Error('You must be signed in to add a candidate');
    }

    const existing = await findCandidatesByLinkedinUrls(user.id, [candidateData.linkedin_url]);
    if (existing.has(candidateData.linkedin_url)) {
      throw new Error('A candidate with this LinkedIn profile already exists in your list.');
    }

    const { total } = await getCandidatesStats(user.id);
    if (total >= maxCandidates) {
      throw new Error(getCandidateLimitTooltip(maxCandidates, isTopTierPlan, t));
    }

    const payload = toCandidateDbPayload(candidateData);
    await createCandidate(user.id, payload);
    await refetch();
    await refetchProfile?.();

    const stats = await getCandidatesStats(user.id);
    setCandidatesStats(stats || { total: 0 });
  };

  // Loading state
  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('candidates.loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">{t('candidates.failedTitle')}</h2>
            <p className="text-muted-foreground">{error}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 md:space-y-8 py-6 md:py-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
                {t('candidates.title')}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {t('candidates.subtitle')}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {selectedCount > 0 && hasAtsIntegration && !isRestricted && (
                isZohoConnected && !hasNoUploadFields ? (
                  <Button
                    type="button"
                    variant="outline"
                    iconName="SquarePen"
                    loading={bulkUploading}
                    disabled={bulkUploading || selectedZohoCount === 0}
                    onClick={handleBulkUploadToZoho}
                  >
                    {bulkUploading
                      ? t('candidates.uploadingToZoho')
                      : selectedZohoCount > 1
                        ? t('candidates.uploadToZohoCount', { count: selectedZohoCount })
                        : t('candidates.uploadToZoho')}
                  </Button>
                ) : (
                  <Tooltip
                    content={
                      !isZohoConnected
                        ? t('candidates.syncNoAtsTooltip')
                        : t('connectAts.uploadNoFieldsTooltip')
                    }
                    align="end"
                    placement="bottom"
                    size="md"
                  >
                    <span className="inline-flex cursor-not-allowed">
                      <Button
                        type="button"
                        variant="outline"
                        iconName="SquarePen"
                        disabled
                      >
                        {t('candidates.uploadToZoho')}
                      </Button>
                    </span>
                  </Tooltip>
                )
              )}
              {selectedCount > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  iconName="Trash2"
                  disabled={bulkUploading}
                  onClick={() => setShowBulkDeleteConfirm(true)}
                  className="text-destructive border-destructive/40 hover:text-destructive hover:bg-destructive/10"
                >
                  {selectedCount > 1 ? t('candidates.deleteSelectedCount', { count: selectedCount }) : t('candidates.deleteSelected')}
                </Button>
              )}
              {!isRestricted && (
                <AddCandidateButton
                  onClick={() => setShowAddModal(true)}
                  isLimitReached={isCandidateLimitReached}
                  maxCandidates={maxCandidates}
                  isTopTierPlan={isTopTierPlan}
                />
              )}
            </div>
          </div>

          {uploadNotice && (
            <div className={`rounded-lg border px-4 py-3 text-sm ${
              uploadNotice.type === 'success'
                ? 'border-success/30 bg-success/10 text-success'
                : 'border-destructive/30 bg-destructive/10 text-destructive'
            }`}>
              {uploadNotice.message}
            </div>
          )}

          {uploadResult && !bulkUploading && (
            <ZohoUploadResult result={uploadResult} />
          )}

          {/* Search and Filters */}
          <SearchAndFilters
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            onSearchSubmit={handleApplySearch}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            contactFilter={contactFilter}
            onContactFilterChange={handleContactFilterChange}
            totalCandidates={sortedCandidates?.length}
            canPrioritize={canPrioritize}
          />
          
          {/* Search indicator */}
          {loading && (
            <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              <span>Searching...</span>
            </div>
          )}
          
          {/* Active filters indicator */}
          {(appliedSearchTerm || statusFilter !== 'all' || contactFilter !== 'all') && !loading && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">{t('candidates.activeFilters')}</span>
              {appliedSearchTerm && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full">
                  {t('candidates.filterSearch', { term: appliedSearchTerm })}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput('');
                      setAppliedSearchTerm('');
                      setCurrentPage(0);
                    }}
                    className="hover:bg-blue-100 rounded-full p-0.5"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              )}
              {statusFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full">
                  {t('candidates.filterStatusChip', { label: statusFilterLabels[statusFilter] || statusFilter })}
                  <button
                    onClick={() => setStatusFilter('all')}
                    className="hover:bg-purple-100 rounded-full p-0.5"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              )}
              {contactFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-700 text-sm rounded-full">
                  {t('candidates.filterContactChip', { label: contactFilterLabels[contactFilter] || contactFilter })}
                  <button
                    onClick={() => setContactFilter('all')}
                    className="hover:bg-teal-100 rounded-full p-0.5"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              )}
              <button
                onClick={handleClearAllFilters}
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                {t('common.clearAll')}
              </button>
            </div>
          )}

          {/* Results summary */}
          {!loading && totalCount > 0 && totalCount <= PAGE_SIZE && (
            <div className="mb-4 text-sm text-muted-foreground">
              {t('candidates.showing')}{' '}
              <span className="font-semibold text-foreground">{totalCount}</span>{' '}
              {totalCount !== 1 ? t('candidates.candidates') : t('candidates.candidate')}
              {(appliedSearchTerm || statusFilter !== 'all' || contactFilter !== 'all') && t('candidates.matchingFilters')}
            </div>
          )}

          {/* Empty state */}
          {!loading && totalCount === 0 ? (
            <div className="bg-card rounded-lg border border-border p-12 text-center">
              <div className="text-muted-foreground mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">{t('candidates.emptyTitle')}</h3>
              <p className="text-sm text-muted-foreground mb-6">
                {appliedSearchTerm || statusFilter !== 'all' || contactFilter !== 'all'
                  ? t('candidates.emptyFiltered') 
                  : t('candidates.emptyDefault')}
              </p>
              {!appliedSearchTerm && statusFilter === 'all' && contactFilter === 'all' && !isRestricted && (
                <div className="flex justify-center">
                  <AddCandidateButton
                    onClick={() => setShowAddModal(true)}
                    isLimitReached={isCandidateLimitReached}
                    maxCandidates={maxCandidates}
                    isTopTierPlan={isTopTierPlan}
                  />
                </div>
              )}
              {(appliedSearchTerm || statusFilter !== 'all' || contactFilter !== 'all') && (
                <button
                  onClick={handleClearAllFilters}
                  className="text-sm text-primary hover:text-accent underline"
                >
                  {t('common.clearAll')}
                </button>
              )}
            </div>
          ) : !loading && (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block">
                <CandidatesTable
                  candidates={paginatedCandidates}
                  onTogglePriority={handleTogglePriority}
                  onDeleteCandidate={handleDeleteCandidate}
                  userPlan={profile?.subscription_plan}
                  isRestricted={isRestricted}
                  lastCheckSort={lastCheckSort}
                  onLastCheckSortChange={handleLastCheckSortChange}
                  selectedIds={selectedIds}
                  onToggleSelect={handleToggleSelect}
                  onToggleSelectAll={handleToggleSelectAll}
                  totalFilteredCount={totalCount}
                />
                <CandidatesPagination
                  currentPage={safePage}
                  pageCount={pageCount}
                  pageSize={PAGE_SIZE}
                  totalCount={totalCount}
                  onPageChange={setCurrentPage}
                />
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden">
                <CandidatesMobileView
                  candidates={paginatedCandidates}
                  onTogglePriority={handleTogglePriority}
                  onDeleteCandidate={handleDeleteCandidate}
                  userPlan={profile?.subscription_plan}
                  isRestricted={isRestricted}
                  selectedIds={selectedIds}
                  onToggleSelect={handleToggleSelect}
                />
                <CandidatesPagination
                  currentPage={safePage}
                  pageCount={pageCount}
                  pageSize={PAGE_SIZE}
                  totalCount={totalCount}
                  onPageChange={setCurrentPage}
                />
              </div>
            </>
          )}
      </div>
      {createPortal(
        <AddCandidateModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddCandidate}
        />,
        document.body
      )}
      {createPortal(
        <ConfirmDialog
          isOpen={showLimitDialog}
          onClose={() => setShowLimitDialog(false)}
          onConfirm={() => setShowLimitDialog(false)}
          title={t('candidates.prioritizationLimitTitle')}
          message={t('candidates.prioritizationLimitMessage', { count: MAX_PRIORITIZED })}
          confirmText={t('common.ok')}
          variant="default"
          hideCancel
        />,
        document.body
      )}
      {createPortal(
        <ConfirmDialog
          isOpen={showBulkDeleteConfirm}
          onClose={() => {
            if (!bulkDeleting) setShowBulkDeleteConfirm(false);
          }}
          onConfirm={handleBulkDeleteConfirm}
          title={selectedCount === 1 ? t('candidates.deleteCandidateTitle') : t('candidates.deleteCandidatesTitle')}
          message={
            selectedCount === 1
              ? t('candidates.deleteSelectedOne')
              : t('candidates.deleteSelectedMany', { count: selectedCount })
          }
          confirmText={bulkDeleting ? t('candidates.deleting') : t('common.delete')}
          cancelText={t('common.cancel')}
        />,
        document.body
      )}
    </MainLayout>
  );
};

export default CandidatesList;